// Contract field registry — the JS half of the contract_fields table.
//
// One definition per field kind, used by BOTH the placement editor and the
// signing page. Two registries would drift, and drift here means a signature
// box that means one thing to the sender and another to the signer.
//
// Keep this in step with the CHECK constraints on contract_fields:
//   field_kind   signature | initials | date_signed | text | checkbox | merge
//   value_source signer | merge_token | system
// The database is the authority; this file exists so the UI does not have to
// re-derive what each kind means.

export const FIELD_KINDS = {
  signature: {
    label: 'Signature',
    hint: 'The signer draws or adopts a signature here.',
    icon: '✎',
    // Default box as a fraction of page size. A signature is wide and short.
    defaultSize: { w: 0.26, h: 0.055 },
    filledBy: 'signer',
    valueColumn: 'value_image_path',
    accent: '#00A0C8',
  },
  initials: {
    label: 'Initials',
    hint: 'A short initialling box, for per-page acknowledgment.',
    icon: '✓',
    defaultSize: { w: 0.09, h: 0.045 },
    filledBy: 'signer',
    valueColumn: 'value_image_path',
    accent: '#31A7D9',
  },
  date_signed: {
    label: 'Date signed',
    hint: 'Filled automatically with the date the signature is recorded.',
    icon: '📅',
    defaultSize: { w: 0.16, h: 0.035 },
    filledBy: 'system',
    valueColumn: 'value_text',
    accent: '#68d391',
  },
  text: {
    label: 'Text',
    hint: 'A box the signer types into.',
    icon: 'T',
    defaultSize: { w: 0.24, h: 0.035 },
    filledBy: 'signer',
    valueColumn: 'value_text',
    accent: '#f6c26b',
  },
  checkbox: {
    label: 'Checkbox',
    hint: 'A box the signer ticks.',
    icon: '☐',
    // Big enough to actually see and hit. The old 0.03x0.022 was ~18pt
    // square, which clipped its own label into unreadability.
    defaultSize: { w: 0.035, h: 0.028 },
    filledBy: 'signer',
    valueColumn: 'value_bool',
    accent: '#a78bfa',
  },
  merge: {
    label: 'Merge field',
    hint: 'Filled from TimeKeep data when the contract is sent.',
    icon: '{ }',
    defaultSize: { w: 0.24, h: 0.035 },
    filledBy: 'merge',
    valueColumn: 'value_text',
    accent: '#63d3f7',
  },
};

export const FIELD_ORDER = ['signature', 'initials', 'date_signed', 'text', 'checkbox', 'merge'];

// Tokens a merge field can resolve. Deliberately a closed list: a free-text
// token would silently render blank when it matched nothing, and a blank on
// an executed agreement is not a thing to discover later.
export const MERGE_TOKENS = {
  '{{counterparty_name}}': 'Client, company or person the contract is with',
  '{{counterparty_address}}':   'Their full address, inline: 1 Cherry Tree Ln, Naperville, FL 02323',
  '{{counterparty_address_1}}': 'Just their first address line',
  '{{counterparty_address_2}}': 'Just their second address line, if they have one',
  '{{counterparty_city}}':      'Their city',
  '{{counterparty_state}}':     'Their state or province',
  '{{counterparty_zip}}':       'Their postal code',
  '{{contract_number}}':   'The contract number',
  '{{contract_title}}':    'The contract title',
  '{{effective_date}}':    'Effective date (today’s date if effective on signature)',
  '{{end_date}}':          'End date, or blank if evergreen',
  '{{org_name}}':          'Your organization’s name',
  '{{org_address}}':       'Your full address, inline',
  '{{org_address_1}}':     'Just your first address line',
  '{{org_address_2}}':     'Just your second address line, if you have one',
  '{{org_city}}':          'Your city',
  '{{org_state}}':         'Your state or province',
  '{{org_zip}}':           'Your postal code',
  '{{signer_name}}':       'The name of the signer this field belongs to',
  '{{signer_email}}':      'That signer’s email',
  '{{signer_title}}':      'That signer’s job title',
  '{{today}}':             'The date the document is viewed',
};

/** value_source for a kind, matching contract_fields_source_valid. */
// Tokens that resolve differently per signer. A document can have several
// counterparties before the org countersigns, so these are meaningless without
// a role attached — {{signer_name}} on its own cannot say whose name.
export const SIGNER_SCOPED_TOKENS = new Set([
  '{{signer_name}}', '{{signer_email}}', '{{signer_title}}',
]);

/** Does this field need a role to be unambiguous? */
export function needsRole(kind, mergeToken) {
  if (valueSourceFor(kind) === 'signer') return true;      // required by the DB
  if (kind === 'date_signed') return true;                 // whose date
  if (kind === 'merge') return SIGNER_SCOPED_TOKENS.has(mergeToken);
  return false;
}

export function valueSourceFor(kind) {
  if (kind === 'merge') return 'merge_token';
  if (kind === 'date_signed') return 'system';
  return 'signer';
}

/** Does this field need a role assigned? Mirrors contract_fields_role_presence. */
export function needsSigner(kind) {
  return valueSourceFor(kind) === 'signer';
}

/** Is this something the signer physically interacts with on the signing page? */
export function isInteractive(kind) {
  return FIELD_KINDS[kind]?.filledBy === 'signer';
}

/**
 * Clamp a page-anchored box so it stays inside the page.
 * contract_fields_coords_normalized rejects anything spilling past the edge,
 * so clamping here turns a database error into a box that simply stops at the
 * margin -- which is what dragging to the edge should do anyway.
 */
export function clampBox({ x, y, w, h }) {
  const cw = Math.min(Math.max(w, 0.005), 1);
  const ch = Math.min(Math.max(h, 0.005), 1);
  return {
    x: Math.min(Math.max(x, 0), 1 - cw),
    y: Math.min(Math.max(y, 0), 1 - ch),
    w: cw,
    h: ch,
  };
}

/** A field row shaped for insert, from a drop on a rendered page. */
export function newPageField({ kind, contractId, orgId, page, x, y, roleId, signerId, mergeToken, sortOrder = 0 }) {
  const def = FIELD_KINDS[kind];
  const box = clampBox({ x, y, w: def.defaultSize.w, h: def.defaultSize.h });
  const source = valueSourceFor(kind);
  return {
    contract_id: contractId,
    org_id: orgId,
    field_kind: kind,
    value_source: source,
    // role_id is the real assignment. signer_id is written alongside it only
    // for the transition -- the edge functions still resolve a signer's fields
    // by signer_id, and dual-writing keeps signing working while they migrate.
    // Once they read role_id, stop writing this and drop the column.
    // A role is required for signer-filled fields and carried by date_signed and
    // signer-scoped merge tokens so they can say WHOSE date or name.
    role_id: needsRole(kind, mergeToken) ? (roleId ?? null) : null,
    signer_id: needsRole(kind, mergeToken) ? (signerId ?? null) : null,
    merge_token: source === 'merge_token' ? (mergeToken ?? null) : null,
    label: def.label,
    is_required: kind !== 'checkbox',
    anchor_kind: 'page',
    page_number: page,
    x_pct: box.x, y_pct: box.y, w_pct: box.w, h_pct: box.h,
    sort_order: sortOrder,
  };
}

/**
 * A field row shaped for insert, anchored to a BLOCK rather than a page.
 *
 * Documents written in TimeKeep have no pages until they are rendered, so a
 * field belongs to a section and `block_offset` orders several fields within
 * it. The renderer turns these into page coordinates at send
 * (_shared/contractBlockRender.ts), after which they are indistinguishable from
 * fields placed on an uploaded PDF.
 *
 * contract_fields_anchor_valid refuses a half-specified anchor: block anchors
 * must carry block_id/block_offset and must NOT carry the page columns.
 */
export function newBlockField({ kind, contractId, orgId, blockId, blockOffset = 0, roleId, signerId, mergeToken, label, isRequired }) {
  const def = FIELD_KINDS[kind];
  const source = valueSourceFor(kind);
  return {
    contract_id: contractId,
    org_id: orgId,
    field_kind: kind,
    value_source: source,
    role_id: needsRole(kind, mergeToken) ? (roleId ?? null) : null,
    // Dual-written while the edge functions still resolve a signer's fields by
    // signer_id -- same transition the page-anchored path is in.
    signer_id: needsRole(kind, mergeToken) ? (signerId ?? null) : null,
    merge_token: source === 'merge_token' ? (mergeToken ?? null) : null,
    label: label ?? def.label,
    is_required: isRequired ?? kind !== 'checkbox',
    anchor_kind: 'block',
    block_id: blockId,
    block_offset: blockOffset,
    sort_order: blockOffset,
  };
}


// An address written the way it is spoken in a sentence: "1 Cherry Tree Ln,
// Suite 200, Naperville, FL 02323". Missing pieces are skipped rather than
// leaving the commas that mark where they would have been.
export function inlineAddress(a) {
  if (!a) return null;
  const cityLine = [a.city, [a.state, a.zip].filter(Boolean).join(' ')]
    .map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  const parts = [a.address_line1, a.address_line2, cityLine, a.country]
    .map((s) => String(s || '').trim()).filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/**
 * Resolve a merge token against the contract. Returns null when a token has
 * nothing to resolve to, so callers can show that rather than rendering an
 * empty box that looks intentional.
 *
 * `counterparty` and `org` carry the address columns when the caller has them;
 * a prospect who is not in the system yet has no address anywhere, so those
 * tokens resolve to null and the line they sit on is dropped.
 */
export function resolveMerge(token, { contract, org, signer, counterpartyLabel, counterparty }) {
  const d = (s) => {
    if (!s) return null;
    const [y, m, day] = String(s).slice(0, 10).split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[Number(m) - 1]} ${Number(day)}, ${y}`;
  };
  const today = () => {
    const n = new Date();
    return d(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`);
  };
  switch (token) {
    case '{{counterparty_name}}': return counterpartyLabel || null;
    case '{{contract_number}}':   return contract?.contract_number || null;
    case '{{contract_title}}':    return contract?.title || null;
    // Effective-on-signature has no date yet, and the agreement will take
    // effect the day it is signed -- so today is the correct answer, not blank.
    case '{{effective_date}}':
      return contract?.effective_date ? d(contract.effective_date)
           : contract?.effective_on_signature ? today() : null;
    case '{{end_date}}':      return contract?.end_date ? d(contract.end_date) : null;
    case '{{org_name}}':      return org?.name || null;
    case '{{signer_name}}':   return signer?.name || null;
    case '{{signer_email}}':  return signer?.email || null;
    case '{{signer_title}}':  return signer?.title || null;
    case '{{today}}':         return today();

    case '{{counterparty_address}}':   return inlineAddress(counterparty);
    case '{{counterparty_address_1}}': return counterparty?.address_line1 || null;
    case '{{counterparty_address_2}}': return counterparty?.address_line2 || null;
    case '{{counterparty_city}}':      return counterparty?.city || null;
    case '{{counterparty_state}}':     return counterparty?.state || null;
    case '{{counterparty_zip}}':       return counterparty?.zip || null;

    case '{{org_address}}':   return inlineAddress(org);
    case '{{org_address_1}}': return org?.address_line1 || null;
    case '{{org_address_2}}': return org?.address_line2 || null;
    case '{{org_city}}':      return org?.city || null;
    case '{{org_state}}':     return org?.state || null;
    case '{{org_zip}}':       return org?.zip || null;

    default: return null;
  }
}
